/* Two-part form: search OMDb for a movie, then review details and submit. */

import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Search, Film, Star, Clock, TrendingUp } from "lucide-react";
import { useMovieSearch, type MovieSearchResult, type MovieDetails } from "@/hooks/useMovieSearch";
import { MovieBrowsePopular } from "@/components/movies/MovieBrowsePopular";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";
import type { PopularMovie } from "@/types/movieDiscover";

interface AddMovieFormProps {
  onSuccess: () => void;
  footerContainer?: HTMLElement | null;
}

export function AddMovieForm({ onSuccess, footerContainer }: AddMovieFormProps) {
  const { results, isSearching, error: searchError, search, fetchDetails } = useMovieSearch();
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedImdbId, setSelectedImdbId] = useState<string | null>(null);
  const [details, setDetails] = useState<MovieDetails | null>(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPopular, setShowPopular] = useState(false);

  const addMovie = useMutation(api.movies.addMovie);
  const fetchImdbDetails = useAction(api.movieDiscover.fetchMovieDetails);

  function handleSearch() {
    search(searchQuery);
    setShowPopular(false);
  }

  async function handleSelectPopularMovie(movie: PopularMovie) {
    setShowPopular(false);
    setSelectedImdbId(null);
    setIsFetchingDetails(true);
    try {
      const movieDetails = await fetchImdbDetails({ imdbId: movie.imdbId });
      if (movieDetails) {
        setDetails(movieDetails);
        setSelectedImdbId(movieDetails.imdbId);
      }
    } catch {
      setDetails(null);
    } finally {
      setIsFetchingDetails(false);
    }
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  }

  async function handleSelectMovie(movie: MovieSearchResult) {
    setSelectedImdbId(movie.imdbId);
    setIsFetchingDetails(true);
    try {
      const movieDetails = await fetchDetails(movie.imdbId);
      setDetails(movieDetails);
    } catch {
      setDetails(null);
    } finally {
      setIsFetchingDetails(false);
    }
  }

  async function handleSubmit() {
    if (!details) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await addMovie({
        imdbId: details.imdbId,
        title: details.title,
        year: details.year,
        director: details.director,
        actors: details.actors,
        plot: details.plot,
        genres: details.genres,
        runtime: details.runtime,
        rating: details.rating,
        poster: details.poster ?? undefined,
      });
      posthog.capture(Events.MOVIE_ADDED, { title: details.title, imdbId: details.imdbId });
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add movie";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 pt-2">
      {/* Search section */}
      <div className="space-y-2">
        <Label>Search OMDb</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Search by movie title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
          />
          <Button
            variant="outline"
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
          >
            {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </Button>
        </div>
        {searchError && <p className="text-destructive text-sm">{searchError}</p>}
      </div>

      {/* Browse Popular toggle */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowPopular(!showPopular)}
        className="w-full"
      >
        <TrendingUp className="size-4" />
        {showPopular ? "Hide Popular Movies" : "Browse Popular Movies"}
      </Button>

      {/* Popular movies section */}
      {showPopular && (
        <MovieBrowsePopular onSelectMovie={handleSelectPopularMovie} />
      )}

      {/* Search results — horizontal scroll of poster cards */}
      {results.length > 0 && !showPopular && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {results.map((movie) => (
            <button
              key={movie.imdbId}
              type="button"
              className={`flex flex-col items-center gap-1 p-2 rounded-md border cursor-pointer hover:bg-muted/50 shrink-0 w-[90px] ${selectedImdbId === movie.imdbId ? "border-primary bg-muted/50" : "border-border"}`}
              onClick={() => handleSelectMovie(movie)}
            >
              {movie.poster ? (
                <img
                  src={movie.poster}
                  alt={movie.title}
                  className="w-12 h-[72px] rounded-sm object-cover bg-muted"
                />
              ) : (
                <div className="w-12 h-[72px] rounded-sm bg-muted flex items-center justify-center">
                  <Film className="size-5 text-muted-foreground/50" />
                </div>
              )}
              <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {movie.title}
              </span>
              <span className="text-[10px] text-muted-foreground text-center truncate w-full">
                {movie.year}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Loading details */}
      {isFetchingDetails && (
        <div className="flex justify-center py-4">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Movie details preview */}
      {details && !isFetchingDetails && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <div className="flex gap-3">
            {details.poster && (
              <img
                src={details.poster}
                alt={details.title}
                className="w-20 h-[120px] rounded-sm object-cover bg-muted shrink-0"
              />
            )}
            <div className="flex flex-col gap-1.5 min-w-0">
              <p className="text-sm font-semibold leading-tight">{details.title}</p>
              <p className="text-xs text-muted-foreground">{details.director} · {details.year}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {details.runtime && (
                  <span className="inline-flex items-center gap-0.5">
                    <Clock className="size-3" />
                    {details.runtime}
                  </span>
                )}
                {details.rating && (
                  <span className="inline-flex items-center gap-0.5">
                    <Star className="size-3 fill-current text-yellow-500" />
                    {details.rating}/10
                  </span>
                )}
              </div>
              {details.genres && (
                <p className="text-xs text-muted-foreground">{details.genres}</p>
              )}
              {details.actors && (
                <p className="text-xs text-muted-foreground truncate">{details.actors}</p>
              )}
            </div>
          </div>

          {details.plot && (
            <p className="text-xs text-muted-foreground leading-relaxed">{details.plot}</p>
          )}
        </div>
      )}

      {submitError && <p className="text-destructive text-sm">{submitError}</p>}

      {footerContainer ? createPortal(
        <Button onClick={handleSubmit} disabled={!details || isSubmitting}>
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add to Queue
        </Button>,
        footerContainer,
      ) : (
        <Button onClick={handleSubmit} disabled={!details || isSubmitting} className="w-full">
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add to Queue
        </Button>
      )}
    </div>
  );
}
